/* oxlint-disable react/static-components -- these render APIs intentionally receive component references */
import { compact } from 'lodash'
import { useState } from 'react'
import { ActivityCard } from 'app/components/activities/ActivityCard'
import { Badge } from 'app/components/Badge'
import { KeyValueDrawer } from 'app/components/activities/KeyValueDrawer'
import { TransactionTypeIcon } from 'app/components/activities/TransactionTypeIcon'
import { LazyProviderById } from 'app/components/shared/LazyProviderById'
import { LazySikkaProvider } from 'app/components/shared/LazySikkaProvider'
import Spinner from 'app/components/Spinner'
import { TreatmentCategoryBadge } from 'app/components/TreatmentCategoryBadge'
import { useCdtCodeMappings } from 'app/hooks/useCdtCodeMappings'
import type { TransactionJson } from 'app/types/entities'
import { getTreatmentCategories } from 'lib/cdtCodeDefaults'
import { formatLedgerAmount } from 'lib/transactionCardContent'
import {
  adjustmentLine,
  brokenAppointmentLabel,
  carriesProcedureCategories,
  paymentLine,
  postedDateLabel,
  procedureLine,
} from 'lib/transactionCardContent'
import type { CardLine } from 'lib/transactionCardContent'

type Props = {
  transaction: TransactionJson
  isFirst?: boolean
  isLast?: boolean
  /**
   * Suppresses the per-card date. The ledger groups by day, so the divider
   * already states it and repeating it on every row is pure noise.
   */
  hideDate?: boolean
}

function ContentBox({ children }: { children: React.ReactNode }) {
  return (
    <div className="w-full items-center rounded-lg border border-default bg-surface px-4 py-3 text-body-xs shadow-sm">
      {children}
    </div>
  )
}

export function TransactionCard({ transaction, isFirst, isLast, hideDate }: Props) {
  const { mappings: customMappings, loading: mappingsLoading } = useCdtCodeMappings()
  const [expanded, setExpanded] = useState(false)
  const isSikka = !!transaction.sikkaOfficeId
  const hasProvider = !!transaction.providerId
  const typeKey = transaction.transactionType?.toLowerCase()

  // Sign/source/date classification lives in lib/transactionCardContent (pure,
  // unit-tested there). Missing amounts stay missing - they never render as $0.
  const postedDate = postedDateLabel(transaction)

  // oxlint-disable-next-line react/no-unstable-nested-components -- render helper intentionally captures parent state
  const Title = () => (
    <div className="flex flex-wrap items-center gap-x-1 text-ui-sm text-strong">
      <span>{transaction.transactionType}</span>
      {isSikka ? (
        <LazySikkaProvider
          sikkaOfficeId={transaction?.sikkaOfficeId}
          sikkaPracticeId={transaction?.sikkaPracticeId}
          providerId={transaction?.providerId}
          showIcon
          showSpinner
        />
      ) : hasProvider ? (
        <LazyProviderById providerId={transaction.providerId} showIcon showSpinner />
      ) : null}
    </div>
  )

  const providerNode = isSikka ? (
    <LazySikkaProvider
      sikkaOfficeId={transaction?.sikkaOfficeId}
      sikkaPracticeId={transaction?.sikkaPracticeId}
      providerId={transaction?.providerId}
      showSpinner
      hideWith
    />
  ) : hasProvider ? (
    <LazyProviderById providerId={transaction.providerId} showSpinner hideWith />
  ) : undefined

  // oxlint-disable-next-line react/no-unstable-nested-components -- render helper intentionally captures parent state
  const PostedLine = () =>
    postedDate ? <div className="mt-1 text-body-xs text-subtle">Posted {postedDate}</div> : null

  // One classified content line: money statements get a bold headline + muted
  // qualifier ("$63.00 received · insurance"); non-money ledger events render
  // as a single muted line. The posting date renders for both.
  // oxlint-disable-next-line react/no-unstable-nested-components -- render helper intentionally captures parent state
  const CardLineView = ({ line }: { line: CardLine }) =>
    line.kind === `amount` ? (
      <div className="flex items-baseline gap-x-1.5">
        <span className="text-ui-sm font-semibold text-strong">{line.headline}</span>
        {line.qualifier && <span className="text-body-xs text-subtle">{line.qualifier}</span>}
      </div>
    ) : (
      <div className="text-body-xs text-subtle">
        {line.text}
        {transaction.procedureCode ? ` · ${transaction.procedureCode}` : ``}
      </div>
    )

  const paymentPreview = () => (
    <ContentBox>
      <CardLineView line={paymentLine(transaction)} />
      <PostedLine />
    </ContentBox>
  )

  const adjustmentPreview = () => {
    const line = adjustmentLine(transaction)
    return (
      <ContentBox>
        <CardLineView line={line} />
        {line.kind === `amount` && transaction.procedureCode && (
          <div className="mt-1 text-body-xs text-subtle">{transaction.procedureCode}</div>
        )}
        <PostedLine />
      </ContentBox>
    )
  }

  /*
   * The same row the grouped cards use: what it was on the left, what it cost
   * right-aligned. A key/value grid made a single procedure look like a
   * different kind of record from the identical procedure one row above it in a
   * collapsed run, and labelled "Amount" and "Procedure Code" as though either
   * needed saying.
   */
  const procedurePreview = () => {
    const line = procedureLine(transaction)
    const descriptor = compact([transaction.procedureCode, transaction.procedureDescription]).join(` · `)

    return (
      <ContentBox>
        <div className="flex items-baseline justify-between gap-x-3">
          <span className="min-w-0 truncate text-body-xs text-subtle">{descriptor}</span>
          {line.kind === `amount` ? (
            <span className="shrink-0 text-ui-sm font-semibold text-strong">{line.headline}</span>
          ) : (
            <span className="shrink-0 text-body-xs text-subtle">{line.text}</span>
          )}
        </div>
        <PostedLine />
      </ContentBox>
    )
  }

  const preview = () => {
    switch (typeKey) {
      case `payment`:
        return paymentPreview()
      case `adjustment`:
        return adjustmentPreview()
      default:
        return procedurePreview()
    }
  }

  // Expanded view: everything the record actually carries (ledger-signed
  // amount), omitting fields the PMS did not populate.
  const drawer = () => (
    <ContentBox>
      <KeyValueDrawer
        map={{
          Amount: transaction.amount == null ? undefined : formatLedgerAmount(transaction.amount),
          'Procedure Code': transaction.procedureCode,
          Description: transaction.procedureDescription,
          Guarantor: transaction.guarantorId,
          'Transaction Sr No': transaction.transactionSrNo,
          Posted: postedDate,
          Provider: expanded ? providerNode : null,
        }}
      />
      {transaction.note && (
        <div className="mt-3 flex w-full flex-col">
          <span className="text-body-xs text-subtle">Note</span>
          <div className="mt-0.5 text-ui-xs text-strong">{transaction.note}</div>
        </div>
      )}
    </ContentBox>
  )

  const brokenLabel = brokenAppointmentLabel(transaction.procedureCode)
  const procedureCodes = carriesProcedureCategories(transaction.transactionType)
    ? compact([transaction.procedureCode])
    : []
  const hasProcedureCodes = procedureCodes.length > 0

  return (
    <ActivityCard
      icon={<TransactionTypeIcon type={transaction.transactionType} />}
      title={<Title />}
      activityDate={hideDate ? undefined : transaction.transactionDate}
      tags={
        hasProcedureCodes && mappingsLoading ? (
          <Spinner size={4} />
        ) : (
          <>
            {/*
              Leads the tags because it is what the row actually is. The CDT
              category behind it reads "Adjunctive General Services", which is
              true of the code range and says nothing about what happened.
            */}
            {brokenLabel && <Badge color="orange" label={brokenLabel} />}
            {getTreatmentCategories(procedureCodes, customMappings).map((category) => (
              <TreatmentCategoryBadge key={category} category={category} />
            ))}
          </>
        )
      }
      preview={preview()}
      drawer={drawer()}
      onClick={() => setExpanded(true)}
      isFirst={isFirst}
      isLast={isLast}
    />
  )
}
